import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';
import * as api from './teamApi';

export function useTeamCreateFieldsQuery() {
  return useQuery({
    queryKey: queryKeys.team.createFields(),
    queryFn: api.fetchTeamCreateFields,
    refetchOnWindowFocus: false,
  });
}

export function useTeamMembersQuery(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: queryKeys.team.list(filters),
    queryFn: () => api.fetchTeamMembers(filters),
    refetchOnWindowFocus: false,
  });
}

export function useTeamMemberQuery(id?: string) {
  return useQuery({
    queryKey: queryKeys.team.detail(id || ''),
    queryFn: () => api.fetchTeamMemberById(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });
}

export function useAssignedClientsQuery(memberId?: string) {
  return useQuery({
    queryKey: queryKeys.team.assignedClients(memberId || ''),
    queryFn: () => api.fetchAssignedClients(memberId!),
    enabled: Boolean(memberId),
    refetchOnWindowFocus: false,
  });
}

export function useMyAssignedClientsQuery(
  filters: Record<string, unknown> = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...queryKeys.team.root, 'my-assigned-clients', filters] as const,
    queryFn: () => api.fetchMyAssignedClients(filters),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
  });
}

export function useCreateTeamMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createTeamMember,
    onSuccess: (data) => {
      toast.success(
        (data as { message?: string })?.message || 'Team member added',
      );
      void qc.invalidateQueries({ queryKey: queryKeys.team.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to add team member'));
    },
  });
}

export function useUpdateTeamMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      payload,
    }: {
      memberId: string;
      payload: Record<string, unknown>;
    }) => api.updateTeamMember(memberId, payload),
    onSuccess: (data) => {
      toast.success(
        (data as { message?: string })?.message || 'Team member updated',
      );
      void qc.invalidateQueries({ queryKey: queryKeys.team.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to update team member'));
    },
  });
}

export function useDeleteTeamMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteTeamMember,
    onSuccess: (data) => {
      toast.success(
        (data as { message?: string })?.message || 'Team member removed',
      );
      void qc.invalidateQueries({ queryKey: queryKeys.team.root });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to remove team member'));
    },
  });
}

export function useAssignClientsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      clientIds,
    }: {
      memberId: string;
      clientIds: string[];
    }) => api.assignClientsToMember(memberId, clientIds),
    onSuccess: (data, vars) => {
      toast.success(
        (data as { message?: string })?.message || 'Clients assigned',
      );
      void qc.invalidateQueries({
        queryKey: queryKeys.team.assignedClients(vars.memberId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.team.detail(vars.memberId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to assign clients'));
    },
  });
}

export function useUnassignClientsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      clientIds,
    }: {
      memberId: string;
      clientIds: string[];
    }) => api.unassignClientsFromMember(memberId, clientIds),
    onSuccess: (data, vars) => {
      toast.success(
        (data as { message?: string })?.message || 'Clients unassigned',
      );
      void qc.invalidateQueries({
        queryKey: queryKeys.team.assignedClients(vars.memberId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.team.detail(vars.memberId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to unassign clients'));
    },
  });
}

export function useUnassignAllClientsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => api.unassignAllClientsFromMember(memberId),
    onSuccess: (data, memberId) => {
      toast.success(
        (data as { message?: string })?.message || 'All clients unassigned',
      );
      void qc.invalidateQueries({
        queryKey: queryKeys.team.assignedClients(memberId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.team.detail(memberId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to unassign all clients'));
    },
  });
}
